import { FormControl, InputLabel, MenuItem, Select, TextField } from '@mui/material'
import { isEmpty, map } from 'lodash'
import { useContext, useEffect, useState } from 'react'
import { createRoleChangeRequest, getRoleChangeRequestOptions } from '../../../api'
import { AppStateContext } from '../../../state/AppProvider'
import CustomDialog from '../CustomDialog/CustomDialog'
import styles from './RequestAccessDialog.module.css'

interface RequestAccessDialogProps {
  open: boolean
  onClose: () => void
}

export const RequestAccessDialog = ({ open, onClose }: RequestAccessDialogProps) => {
  const appStateContext = useContext(AppStateContext)

  const [organizationName, setOrganizationName] = useState(
    appStateContext?.state.changeRoleRequest?.organizationName ?? '',
  )
  const [ou, setOu] = useState(appStateContext?.state.changeRoleRequest?.ou ?? '')
  const [ouOptions, setOuOptions] = useState<string[]>([])
  const [functionField, setFunctionField] = useState(appStateContext?.state.changeRoleRequest?.function ?? '')
  const [requestReason, setRequestReason] = useState(appStateContext?.state.changeRoleRequest?.requestReason ?? '')

  useEffect(() => {
    if (open) {
      getRoleChangeRequestOptions().then(payload => {
        setOuOptions(payload?.options?.ou ?? [])
      })
    }
  }, [open])

  const handleRequestExperimenter = () => {
    appStateContext?.dispatch({
      type: 'SET_CHANGE_ROLE_REQUEST',
      payload: { organizationName, ou, function: functionField, requestReason },
    })
    onClose()
    createRoleChangeRequest({
      organization_name: organizationName,
      ou,
      function: functionField,
      request_reason: requestReason,
    })
      .then(res => {
        if (res.success) {
          appStateContext?.dispatch({
            type: 'SET_CHANGE_ROLE_REQUEST',
            payload: { organizationName: '', ou: '', function: '', requestReason: '' },
          })
          appStateContext?.dispatch({
            type: 'SET_HAS_PENDING_ROLE_CHANGE_REQUEST',
            payload: true,
          })
          appStateContext?.dispatch({
            type: 'SET_SNACKBAR_STATE',
            payload: { message: 'Access request sent successfully', severity: 'success' },
          })
        } else {
          appStateContext?.dispatch({
            type: 'SET_SNACKBAR_STATE',
            payload: { message: 'Access request failed. Try again later.', severity: 'warning' },
          })
        }
      })
      .catch(() => {
        appStateContext?.dispatch({
          type: 'SET_SNACKBAR_STATE',
          payload: { message: 'Access request failed. Try again later.', severity: 'warning' },
        })
      })
  }

  return (
    <CustomDialog
      isRequestDialog
      open={open}
      setClose={onClose}
      title="Request Access"
      primaryAction={handleRequestExperimenter}
      primaryActionButtonText="Request"
      secondaryAction={onClose}
      secondaryActionButtonText="Cancel"
      primaryActionDisabled={
        isEmpty(organizationName) || isEmpty(ou) || isEmpty(functionField) || isEmpty(requestReason)
      }
    >
      <div className={styles.container}>
        <TextField
          className={styles.textFieldFilled}
          label="Full Name"
          value={appStateContext?.state.user?.name}
          disabled
          variant="filled"
        />
        <TextField
          className={styles.textFieldFilled}
          label="Email ID"
          value={appStateContext?.state.user?.email}
          disabled
          variant="filled"
        />
        <TextField
          size="small"
          placeholder="Enter your organization name"
          label="Organization Name"
          slotProps={{
            inputLabel: {
              shrink: true,
            },
          }}
          value={organizationName}
          onChange={e => setOrganizationName(e.target.value)}
        />
        <div className={styles.selectsContainer}>
          <FormControl style={{ width: '220px' }} size="small">
            <InputLabel shrink>OU</InputLabel>
            <Select
              displayEmpty
              renderValue={(value: unknown) => {
                if (isEmpty(value)) {
                  return <p className={styles.textFieldFilledLabel}>Select</p>
                }
                return <>{value}</>
              }}
              value={ou}
              onChange={e => setOu(e.target.value as string)}
              label="OU"
              size="small"
            >
              {map(ouOptions, option => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            slotProps={{
              inputLabel: {
                shrink: true,
              },
            }}
            placeholder="Enter your function"
            label="Function"
            size="small"
            value={functionField}
            onChange={e => setFunctionField(e.target.value)}
          />
        </div>
        <TextField
          multiline
          rows={2}
          label="Request Reason"
          placeholder="Enter the reason of your request"
          slotProps={{
            inputLabel: {
              shrink: true,
            },
            formHelperText: {
              className: styles.helperText,
            },
            htmlInput: { maxLength: 100 },
          }}
          value={requestReason}
          onChange={e => setRequestReason(e.target.value)}
          helperText={`${requestReason.length}/100`}
        />
      </div>
    </CustomDialog>
  )
}

